/**
 * Plain-language summary of what `POST /admin/units` auto-linked (ISS-046
 * `auto_link`, T-058 #4). Shown in the "Unit created" toast so the person adding
 * a trailer learns right then whether its Sol-Ark and Starlink came along — and,
 * when they didn't, what happens next. The server decides the outcome; this only
 * renders it.
 */
import type { AutoLinkResult, AutoLinkOutcome } from '@/lib/adminApi';

type SystemKey = 'solark' | 'starlink';
const SYSTEM_LABEL: Record<SystemKey, string> = { solark: 'Sol-Ark', starlink: 'Starlink' };

/** What the person should do (or expect) for one system that did not link. */
function explainMiss(system: SystemKey, unitCode: string, outcome: AutoLinkOutcome | undefined): string {
  const label = SYSTEM_LABEL[system];
  // An absent outcome (older API) is indistinguishable from "nothing matched".
  if (!outcome || outcome.reason === 'no_match' || outcome.reason === undefined) return `no ${label} named ${unitCode} yet`;
  switch (outcome.reason) {
    case 'ambiguous':
      return `${outcome.candidates ?? 'several'} unlinked ${label} devices match ${unitCode} — pick one in System Devices`;
    case 'conflict':
      return system === 'solark'
        ? `the inverter for this Sol-Ark plant is linked to another unit — resolve in System Devices`
        : `the ${label} is linked to another unit — resolve in System Devices`;
    case 'raced':
      // The claim lost to a write that landed first — whoever won owns the device now.
      return `another unit claimed the ${label} first — check System Devices`;
    case 'error':
      return `${label} auto-link failed — check System Devices`;
    default:
      return `${label} auto-link did not complete (${outcome.reason}) — check System Devices`;
  }
}

/** One sentence for the create toast. */
export function describeUnitCreated(unitCode: string, autoLink: AutoLinkResult | null | undefined): string {
  const head = `Unit ${unitCode} created`;
  if (!autoLink) return head;

  const linked: string[] = [];
  const misses: string[] = [];
  for (const system of ['solark', 'starlink'] as SystemKey[]) {
    const outcome = autoLink[system];
    if (outcome?.linked) linked.push(SYSTEM_LABEL[system]);
    else misses.push(explainMiss(system, unitCode, outcome));
  }

  if (misses.length === 0) return `${head} — ${linked.join(' and ')} linked`;

  const onlyNoMatch = misses.length === 2 && [autoLink.solark, autoLink.starlink].every((o) => !o?.reason || o.reason === 'no_match');
  if (onlyNoMatch) {
    return `${head} — no discovered devices named ${unitCode} yet; they link automatically when they come online under that name`;
  }
  const parts = [...(linked.length ? [`${linked.join(' and ')} linked`] : []), ...misses];
  return `${head} — ${parts.join(' · ')}`;
}
